import { memo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { IconButton } from "@mui/material";
import Button from "@mui/material/Button";

import { useAppDispatch } from "@zenith/app/hooks";
import { Copy } from "@zenith/assets/icons/components";
import {
  usePaymentLinkContext,
  usePaymentsContext,
} from "@zenith/context/global-context/hooks";
import { emptyCart } from "@zenith/features/Payments/components";
import {
  DASHBOARD,
  COLLECT_PAYMENT,
  PAYMENT_OVERVIEW,
  PAYMENT_PAYMENT_LINK,
  PAYMENT_LINK_SUCCESS,
} from "@zenith/routes/paths";
import { formatCurrency } from "@zenith/utils";
import { ButtonComponent, SpinnerComponent, ToastSnackbarComponent } from "@zenith/components";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import { pollPaymentStatus } from "@zenith/utils/Polls/pollPaymentStatus";
import { OperationDTO } from "@zenith/services/transactions";
import { useNotificationContext } from "@zenith/context";
import "@zenith/utils/ExtensionMethods";

const PaymentLinkPage = memo(function PaymentLinkPage() {
  const { paymentsContext, clearPaymentsContext } = usePaymentsContext();
  const { paymentLinkContext, setPaymentLinkContext } =
    usePaymentLinkContext();
  const {
    operationId: contextOperationId,
    transactionInfo: { paymentLinkUrl },
  } = paymentsContext;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { setOpen } = useNotificationContext();

  const [copyFeedback, setCopyFeedback] = useState<string>("");
  const [isConfirming, setIsConfirming] = useState(false);
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  const notify = (title: string, message: string, status: "success" | "error" | "warning") => {
    setToasterData({ title, message, status });
    setOpen(true);
  };

  const operationId =
    contextOperationId ||
    location.state?.operationId ||
    paymentsContext.operationDetails?.operationId ||
    localStorage.getItem("paymentLinkLastOpId") ||
    sessionStorage.getItem("paymentLinkCurrentOpId");

  if (!operationId) {
    return <Navigate to={COLLECT_PAYMENT} replace />;
  }

  const { totalAmount, amount } = location.state || {};
  const displayAmount = totalAmount || amount || 0;

  function handleCopyClick(): void {
    const codeToCopy = paymentLinkUrl as string;
    const textArea = document.createElement("textarea");
    textArea.value = codeToCopy;
    document.body.appendChild(textArea);
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const feedback = successful
        ? "Link copied to clipboard!"
        : "Copy failed.";
      setCopyFeedback(feedback);
    } catch (err) {
      console.error("Copy failed: ", err);
      setCopyFeedback("Copy failed.");
    }

    document.body.removeChild(textArea);
  }

  function handleGoToDashboard(): void {
    clearPaymentsContext();
    navigate(DASHBOARD);
    dispatch(emptyCart());
  }

  const handleConfirmPayment = async () => {
    if (!operationId) {
      console.error("Operation ID missing");
      return;
    }

    setIsConfirming(true);
    try {
      const response = await pollPaymentStatus(operationId, true);

      if (response.status === OperationDTO.status.SUCCESS) {
        setPaymentLinkContext({
          ...paymentLinkContext,
          operationDetails: response.data,
          totalAmount: response.data?.totalAmount || displayAmount,
          transactionId: response.data?.transactionId || paymentLinkContext.transactionId,
        });

        navigate(PAYMENT_LINK_SUCCESS, {
          state: {
            operationId: response.data?.operationId || operationId,
            transactionId: response.data?.transactionId,
            totalAmount: response.data?.totalAmount || displayAmount,
            paymentMethod: OperationDTO.paymentMethod.PAYMENT_LINK,
            timestamp: new Date().toISOString(),
            isPaymentLink: true,
          },
        });
        return;
      }

      if (response.status === OperationDTO.status.PENDING) {
        notify("Payment Pending", "The payment is still pending confirmation. Please try again later.", "warning");
      } else {
        notify("Payment Status", response.error?.message || "Payment could not be confirmed at this time.", "error");
      }
    } catch (error) {
      console.error("Error confirming payment:", error);
      notify("Error", "Failed to confirm payment. Please try again.", "error");
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div>
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />

      <ButtonComponent
        id="back-to-payment-overview"
        onClick={() => {
          navigate(PAYMENT_OVERVIEW, {
            state: { prevPath: PAYMENT_PAYMENT_LINK },
          });
        }}
        label="Back"
        size="large"
        type="tertiary"
        iconStart={<ChevronLeft />}
      />

      <div className="mt-12 mb-16 flex justify-center">
        <div className="flex w-full flex-col rounded-2.5xl border border-neutral-200 px-8 pt-6 pb-8 md:w-120">
          <h1 className="mb-4 text-2xl font-medium text-neutral-900">
            Payment Link
          </h1>
          <p className="mb-12 text-sm font-normal text-neutral-500">
            Collect payment through this link
          </p>
          <hr className="mb-8 border-neutral-200"></hr>
          <div className="mb-8 flex justify-between">
            <h2 className="text-base font-normal text-neutral-400">Amount</h2>
            <h2 className="text-main-200-925 text-2xl font-medium">
              {formatCurrency(displayAmount)}
            </h2>
          </div>
          <div>
            <div className="mb-4 flex flex-row rounded-2xl border border-neutral-200 py-8 font-medium text-neutral-900">
              <div className="flex w-full flex-col px-6">
                <div className="flex h-auto w-full flex-col items-center justify-center">
                  {!paymentLinkUrl ? (
                    <SpinnerComponent size={64} type={"primary"} />
                  ) : (
                    paymentLinkUrl
                  )}
                </div>
              </div>
            </div>
            <div className="mb-4 flex flex-col items-center">
              <IconButton onClick={handleCopyClick}>
                <Copy />
              </IconButton>
              <Button
                onClick={handleCopyClick}
                className="text-sm font-normal normal-case text-neutral-600"
              >
                Copy
              </Button>
              {copyFeedback && (
                <p className="mt-2 text-sm text-green-600">{copyFeedback}</p>
              )}
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-4">
            <div className="flex w-full justify-center gap-4">
              <ButtonComponent
                label="Confirm Payment"
                onClick={handleConfirmPayment}
                id="confirm-payment-button"
                size="large"
                type="primary"
                disabled={isConfirming}
                isLoading={isConfirming}
                className="flex-1 max-w-[212px]"
              />
              <ButtonComponent
                label="Go to Dashboard"
                onClick={handleGoToDashboard}
                id="goto-dashboard-button"
                size="large"
                type="secondary"
                className="flex-1 max-w-[212px]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PaymentLinkPage;
